import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api as botApi } from "@/api/bot-base-api-request";
import { Wallet as ApiWallet } from "@/api/wallet-screener/types";
import { ApiSupportedChain, useWallet, WalletType } from "@/providers/wallet";
import {
  useWalletDiscoveryFilter,
  WalletDiscoveryFiltersParams,
} from "@/providers/wallet-discovery-filter/wallet-discovery-filter-provider";
import { fetchBotCopyTradeData } from "@/api/bot/bot-get-copytrade-data";
import { WALLET_SCREENER_LIMIT } from "@/api/wallet-screener/wallet-screener";
import { useEffect, useMemo, useRef, useState } from "react";

export interface CopyTradeWallet extends ApiWallet {
  isActive: boolean;
  // Information about which wallet is copying this wallet
  sourceWallet?: {
    address: string;
    chain: WalletType;
    chainId: number;
  };
  _id?: string; // Unique identifier for the wallet, if available
  // Statistics data now comes directly from the backend
  stats?: {
    totalAmountInvestUsd: number;
    totalTrades: number;
    winRate: number;
    realizedPnl: number;
    realizedRoi: number;
    unrealizedPnl: number;
    unrealizedRoi: number;
    netPnl: number;
    netRoi: number;
  };
}

// Sorting types
export type SortColumn =
  | "invested"
  | "trades"
  | "winRate"
  | "realizedPNL"
  | "realizedROI"
  | "unrealizedPNL"
  | "unrealizedROI"
  | "netPNL"
  | "netROI";

export type SortDirection = "asc" | "desc";

export interface SortConfig {
  column: SortColumn;
  direction: SortDirection;
}

// Query keys for better caching and invalidation
export const copyTradeKeys = {
  all: ["copyTradeWallets"] as const,
  filtered: (
    limit: number,
    userId: string,
    currentWalletAddress: string | undefined,
    chainId: number,
    filters: any,
    searchWallet: string,
    chains: string[]
  ) =>
    [
      ...copyTradeKeys.all,
      limit,
      userId,
      currentWalletAddress,
      chainId,
      filters,
      searchWallet,
      chains,
    ] as const,
};

export const useCopyTradeWallets = (isAllSelected?: boolean) => {
  const {
    userId,
    currentWallet,
    ethereumAddresses,
    solanaAddresses,
    appPubKey,
  } = useWallet();
  const { filters, searchWallet, chains } = useWalletDiscoveryFilter();
  const queryClient = useQueryClient();

  // Add state for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: "netROI",
    direction: "desc",
  });

  // Use refs to track previous values for comparison
  const prevWalletRef = useRef<string | null>(currentWallet?.address || null);
  const prevFiltersRef = useRef(filters);
  const prevSearchRef = useRef(searchWallet);
  const prevChainsRef = useRef(chains);
  const prevIsAllSelectedRef = useRef(isAllSelected);

  // Check if any non-sort filters are defined
  const hasActiveFilters = () => {
    const filterKeys: (keyof WalletDiscoveryFiltersParams)[] = [
      "invested",
      "trades",
      "netPnlPercent",
      "netPnl",
      "peakPnlPercent",
      "peakPnl",
      "realizedPnlPercent",
      "realizedPnl",
      "unrealizedPnlPercent",
      "unrealizedPnl",
      "winRate",
    ];

    return filterKeys.some((key) => {
      if (!filters[key]) return false;
      const filterValue = filters[key];
      if (
        filterValue &&
        typeof filterValue === "object" &&
        ("min" in filterValue || "max" in filterValue) &&
        (filterValue.min !== undefined || filterValue.max !== undefined) &&
        (filterValue.min !== 0 || filterValue.max !== 0)
      ) {
        return true;
      }
      return false;
    });
  };

  // Stable query key that only changes when dependencies actually change
  const queryKey = copyTradeKeys.filtered(
    WALLET_SCREENER_LIMIT,
    userId || "",
    currentWallet?.address,
    currentWallet?.botChainId || 0,
    filters,
    searchWallet || "",
    chains
  );

  // Helper function to compare values safely (handling undefined/null)
  const compareValues = (a: any, b: any) => {
    // Handle undefined or null values
    if (a === undefined || a === null) return -1;
    if (b === undefined || b === null) return 1;

    // Compare numbers
    if (typeof a === "number" && typeof b === "number") {
      return a - b;
    }

    // Compare strings
    if (typeof a === "string" && typeof b === "string") {
      return a.localeCompare(b);
    }

    // Default
    return 0;
  };

  // Function to handle column sort toggle
  const handleSortToggle = (column: SortColumn) => () => {
    // If clicking the same column, toggle direction
    if (sortConfig.column === column) {
      setSortConfig({
        column,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    } else {
      // If clicking a new column, default to descending
      setSortConfig({
        column,
        direction: "desc",
      });
    }
  };

  // Process wallets with additional data
  const {
    data: fetchedWallets,
    isLoading,
    isRefetching: isRefreshing,
    refetch: refresh,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      let allCopyTradeData: any[] = [];
      const processedWallets = new Set(); // Track processed wallet addresses to avoid duplicates

      if (isAllSelected) {
        // Fetch Ethereum wallet data
        // const ethereumDataResults = await Promise.all(
        //   ethereumAddresses.map(async (wallet) => {
        //     try {
        //       const fetchedData = await fetchBotCopyTradeData(botApi, {
        //         userId,
        //         userWallet: wallet?.address,
        //         chainId: wallet?.botChainId,
        //         appPubKey,
        //       });

        //       // Add source wallet information to each copied wallet
        //       const copyTradeData = (
        //         (fetchedData as any)?.copyTradeData ?? []
        //       ).map((item: any) => ({
        //         ...item,
        //         sourceWallet: {
        //           address: wallet?.address,
        //           chain: WalletType.ETH,
        //           chainId: wallet?.botChainId,
        //         },
        //       }));

        //       return copyTradeData;
        //     } catch (error) {
        //       console.error(
        //         `Error fetching ETH data for wallet ${wallet?.address}:`,
        //         error
        //       );
        //       return [];
        //     }
        //   })
        // );

        // // Flatten the results but avoid duplicates
        // ethereumDataResults.forEach((result) => {
        //   result.forEach((item: any) => {
        //     // Create unique key based on both wallet addresses
        //     const uniqueKey = `${item.address}-${item.sourceWallet.address}`;
        //     if (!processedWallets.has(uniqueKey)) {
        //       processedWallets.add(uniqueKey);
        //       allCopyTradeData.push(item);
        //     }
        //   });
        // });

        const solanaDataResults = await Promise.all(
          solanaAddresses.map(async (wallet) => {
            try {
              const fetchedData = await fetchBotCopyTradeData(botApi, {
                userId,
                userWallet: wallet?.address,
                chainId: wallet?.botChainId,
                appPubKey,
              });

              // Add source wallet information to each copied wallet
              const copyTradeData = (
                (fetchedData as any)?.copyTradeData ?? []
              ).map((item: any) => ( {
                
                ...item,
                sourceWallet: {
                  address: wallet?.address,
                  chain: WalletType.SOL,
                  chainId: wallet?.botChainId,
                },
              }));

              return copyTradeData;
            } catch (error) {
              console.error(
                `Error fetching SOL data for wallet ${wallet?.address}:`,
                error
              );
              return [];
            }
          })
        );

        // Flatten the results while avoiding duplicates
        solanaDataResults.forEach((result) => {
          result.forEach((item: any) => {
            // Create unique key based on both wallet addresses
            const uniqueKey = `${item.address}-${item.sourceWallet.address}`;
            if (!processedWallets.has(uniqueKey)) {
              processedWallets.add(uniqueKey);
              allCopyTradeData.push(item);
            }
          });
        });
      } else {
        // Fetch data for a single wallet
        const fetchedData = await fetchBotCopyTradeData(botApi, {
          userId,
          userWallet: currentWallet?.address,
          chainId: currentWallet?.botChainId,
          appPubKey,
        });

        // Add source wallet information to each copied wallet
        allCopyTradeData = ((fetchedData as any)?.copyTradeData ?? []).map(
          (item: any) => ( {

            ...item,
            sourceWallet: {
              address: currentWallet?.address,
              chain:
                currentWallet?.botChainId === 1
                  ? WalletType.ETH
                  : WalletType.SOL,
              chainId: currentWallet?.botChainId,
            },
          })
        );
      }

      // Skip processing if no data was found
      if (allCopyTradeData.length === 0) {
        return [];
      }

      // Check if there are any non-sort filters active
      const shouldFilterIncompleteWallets = hasActiveFilters();

      // Create a map to store enriched wallet data with composite keys to avoid duplicates
      const enrichedWalletsMap = new Map();

      // Process each wallet and use uniqueKey for deduplication
      allCopyTradeData.forEach((item: any) => {
        // Generate a unique key that incorporates both target and source wallet
        const uniqueKey = `${item.address}-${item.sourceWallet?.address}`;

        // Skip if we've already processed this unique combination
        if (enrichedWalletsMap.has(uniqueKey)) {
          return;
        }

        // Get stats from the backend response (now included directly)
        const stats = item.stats;

        // Determine chain
        const chain = item.chain || item.sourceWallet?.chain || WalletType.ETH;

        // Check if the wallet passes the filters using the stats data
        const shouldInclude =
          !shouldFilterIncompleteWallets ||
          (stats &&
            (!filters.invested ||
              ((filters.invested.min === undefined ||
                stats.totalAmountInvestUsd >= filters.invested.min) &&
                (filters.invested.max === undefined ||
                  stats.totalAmountInvestUsd <= filters.invested.max))) &&
            (!filters.trades ||
              ((filters.trades.min === undefined ||
                stats.totalTrades >= filters.trades.min) &&
                (filters.trades.max === undefined ||
                  stats.totalTrades <= filters.trades.max))) &&
            (!filters.netPnlPercent ||
              ((filters.netPnlPercent.min === undefined ||
                stats.netRoi >= filters.netPnlPercent.min) &&
                (filters.netPnlPercent.max === undefined ||
                  stats.netRoi <= filters.netPnlPercent.max))) &&
            (!filters.netPnl ||
              ((filters.netPnl.min === undefined ||
                stats.netPnl >= filters.netPnl.min) &&
                (filters.netPnl.max === undefined ||
                  stats.netPnl <= filters.netPnl.max))) &&
            (!filters.realizedPnlPercent ||
              ((filters.realizedPnlPercent.min === undefined ||
                stats.realizedRoi >= filters.realizedPnlPercent.min) &&
                (filters.realizedPnlPercent.max === undefined ||
                  stats.realizedRoi <= filters.realizedPnlPercent.max))) &&
            (!filters.realizedPnl ||
              ((filters.realizedPnl.min === undefined ||
                stats.realizedPnl >= filters.realizedPnl.min) &&
                (filters.realizedPnl.max === undefined ||
                  stats.realizedPnl <= filters.realizedPnl.max))) &&
            (!filters.unrealizedPnlPercent ||
              ((filters.unrealizedPnlPercent.min === undefined ||
                stats.unrealizedRoi >= filters.unrealizedPnlPercent.min) &&
                (filters.unrealizedPnlPercent.max === undefined ||
                  stats.unrealizedRoi <= filters.unrealizedPnlPercent.max))) &&
            (!filters.unrealizedPnl ||
              ((filters.unrealizedPnl.min === undefined ||
                stats.unrealizedPnl >= filters.unrealizedPnl.min) &&
                (filters.unrealizedPnl.max === undefined ||
                  stats.unrealizedPnl <= filters.unrealizedPnl.max))) &&
            (!filters.winRate ||
              ((filters.winRate.min === undefined ||
                stats.winRate >= filters.winRate.min) &&
                (filters.winRate.max === undefined ||
                  stats.winRate <= filters.winRate.max))));

        // If should not include due to filtering, skip this wallet
        if (!shouldInclude) {
          return;
        }

        // Store the enriched wallet with source information using the composite key
        enrichedWalletsMap.set(uniqueKey, {
          ...item,
          address: item.address,
          chain,
          chainSymbol:
            chain === WalletType.ETH
              ? ApiSupportedChain.ETH
              : ApiSupportedChain.SOL,
          isFavourite: false,
          isActive: item?.isOn === true,
          sourceWallet: item.sourceWallet,
          stats,

          // Map the stats to the structure expected by the UI
          invested: stats?.totalAmountInvestUsd,
          trades: stats?.totalTrades,
          realizedPNL: {
            amount: stats?.realizedPnl,
            percentage: stats?.realizedRoi,
          },
          unrealizedPNL: {
            amount: stats?.unrealizedPnl,
            percentage: stats?.unrealizedRoi,
          },
          netPNL: {
            amount: stats?.netPnl,
            percentage: stats?.netRoi,
          },
          // Keep peakPNL for UI compatibility but it's not in the actual API response
          peakPNL: {
            amount: 0,
            percentage: 0,
          },
          winRate: stats?.winRate,
        });
      });

      // Convert the map values to array
      const enrichedWallets = Array.from(
        enrichedWalletsMap.values()
      ) as CopyTradeWallet[];

      // Filter by chains
      let filteredResult = enrichedWallets.filter(
        (wallet) => wallet.chain && chains.includes(wallet.chain)
      );

      // do not include wallet that do not have _id field
      filteredResult = filteredResult.filter((wallet) => wallet?._id);

      // Filter by search wallet
      if (searchWallet) {
        filteredResult = filteredResult.filter((wallet) =>
          wallet.address.toLowerCase().includes(searchWallet.toLowerCase())
        );
      }

      return filteredResult;
    },

    // if the wallet does not have createdAt field do not use it
    enabled:
      !!userId && (ethereumAddresses.length > 0 || solanaAddresses.length > 0),
    staleTime: 5 * 60 * 1000, // 5 minutes

  });

  // Effect to handle dependencies changes for cache invalidation
  useEffect(() => {
    const walletChanged = prevWalletRef.current !== currentWallet?.address;
    const isAllSelectedChanged = prevIsAllSelectedRef.current !== isAllSelected;
    const filtersChanged = !(prevFiltersRef.current === filters);
    const searchChanged = prevSearchRef.current !== searchWallet;
    const chainsChanged = !(prevChainsRef.current === chains);

    // Only invalidate cache when relevant dependencies change
    if (walletChanged || isAllSelectedChanged) {
      queryClient.invalidateQueries({ queryKey: copyTradeKeys.all });

      // Update refs
      prevWalletRef.current = currentWallet?.address || null;
      prevIsAllSelectedRef.current = isAllSelected;
    }

    // Update other refs without triggering invalidation
    if (filtersChanged) {
      prevFiltersRef.current = filters;
    }

    if (searchChanged) {
      prevSearchRef.current = searchWallet;
    }

    if (chainsChanged) {
      prevChainsRef.current = chains;
    }
  }, [
    currentWallet?.address,
    isAllSelected,
    filters,
    searchWallet,
    chains,
    queryClient,
  ]);

  // Method to refresh the data selectively
  const refreshWallets = async () => {
    // Only invalidate the specific query instead of all queries
    await queryClient.invalidateQueries({
      queryKey: queryKey,
    });
    await refresh();
  };

  // Sorting function that returns the sorted wallets
  const sortedWallets = useMemo(() => {
    if (!fetchedWallets) return [];

    const walletsCopy = [...fetchedWallets];

    return walletsCopy.sort((a, b) => {
      let comparison = 0;

      // Handle sorting based on column
      switch (sortConfig.column) {
        case "invested":
          comparison = compareValues(a.invested, b.invested);
          break;
        case "trades":
          comparison = compareValues(a.trades, b.trades);
          break;
        case "winRate":
          comparison = compareValues(a.winRate, b.winRate);
          break;
        case "realizedPNL":
          comparison = compareValues(
            a.realizedPNL?.amount,
            b.realizedPNL?.amount
          );
          break;
        case "realizedROI":
          comparison = compareValues(
            a.realizedPNL?.percentage,
            b.realizedPNL?.percentage
          );
          break;
        case "unrealizedPNL":
          comparison = compareValues(
            a.unrealizedPNL?.amount,
            b.unrealizedPNL?.amount
          );
          break;
        case "unrealizedROI":
          comparison = compareValues(
            a.unrealizedPNL?.percentage,
            b.unrealizedPNL?.percentage
          );
          break;
        case "netPNL":
          comparison = compareValues(a.netPNL?.amount, b.netPNL?.amount);
          break;
        case "netROI":
          comparison = compareValues(
            a.netPNL?.percentage,
            b.netPNL?.percentage
          );
          break;
        default:
          comparison = 0;
      }

      // Apply direction (asc or desc)
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [fetchedWallets, sortConfig]);

  return {
    wallets: sortedWallets,
    isLoading: isLoading && !!userId && (ethereumAddresses.length > 0 || solanaAddresses.length > 0),
    error,
    isRefreshing,
    refreshWallets,
    sortConfig,
    handleSortToggle,
  };
};
